export interface DirtyFieldsObject {
  [key: string]: boolean | DirtyFieldsObject;
}
export type DirtyFieldsValue = boolean | DirtyFieldsObject;

/**
 * Merges defaultValues with currentValues, keeping user edits where dirtyFields
 * indicates the field was modified. Used when resetting a form with new defaults
 * while preserving unsaved user changes.
 */
export function mergeWithUserEdits<T extends Record<string, unknown>>(
  defaultValues: T,
  currentValues: T,
  dirtyFields: DirtyFieldsValue | undefined,
): T {
  // Early return: no dirty info → use defaults; entire object dirty (true) → use current.
  // React Hook Form marks whole branches as `true` when it can't provide field-level granularity
  // (e.g. for dynamic keys in a Record), so we treat `true` as "all user edits, keep current".
  if (!dirtyFields || dirtyFields === true) {
    return dirtyFields === true ? currentValues : defaultValues;
  }

  const result = { ...defaultValues } as Record<string, unknown>;

  for (const key of Object.keys(currentValues) as (keyof T)[]) {
    const def = defaultValues[key] as unknown;
    const cur = currentValues[key] as unknown;
    const dirty = (dirtyFields as Record<string, DirtyFieldsValue>)[
      key as string
    ];

    // Branch 1: This key is fully dirty (RHF marks whole value as true). Use current.
    if (dirty === true) {
      (result as Record<string, unknown>)[key as string] = cur;
    }
    // Branch 2: dirty is a nested object with field-level info; cur is an object. Recurse.
    else if (
      dirty &&
      typeof dirty === "object" &&
      !Array.isArray(dirty) &&
      cur !== null &&
      typeof cur === "object" &&
      !Array.isArray(cur)
    ) {
      (result as Record<string, unknown>)[key as string] = mergeWithUserEdits(
        (def ?? {}) as Record<string, unknown>,
        cur as Record<string, unknown>,
        dirty as Record<string, DirtyFieldsValue>,
      );
    }
    // Branch 3: Key exists in current but not in defaults (user added it). Use current.
    else if (cur !== undefined && def === undefined) {
      (result as Record<string, unknown>)[key as string] = cur;
    }
  }

  return result as T;
}
